import {identity} from 'lodash';
import type {BreakpointID, CssPropertyMap} from '@packages/systems/style/types';

import type {PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';
import {push} from '@packages/systems/core/utils/ImmUtils';
import {Map as IMap} from 'immutable';

import {getStyleProperties} from '@packages/systems/style/utils';
import {isElementGridDisplay} from '@packages/shared/utils/isNodeGridDisplay';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {Expressions} from '@packages/systems/wfdl';
import {getChildren as getEvaluatedChildren} from '@packages/utilities/data-utils';
import {getChildren} from '@packages/utilities/platform';
import {
  isDOM,
  isSlot,
  isStringElement,
} from '@packages/utilities/elementTypeUtils';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';
import type {EvaluatedData} from '@packages/systems/render';

export const htmlElementsAcceptingChildren = new Set([
  'a',
  'abbr',
  'address',
  'article',
  'aside',
  'audio',
  'b',
  'bdi',
  'bdo',
  'blockquote',
  'body',
  'button',
  'canvas',
  'caption',
  'cite',
  'code',
  'colgroup',
  'data',
  'datalist',
  'dd',
  'del',
  'details',
  'dfn',
  'dialog',
  'div',
  'dl',
  'dt',
  'em',
  'embed',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'head',
  'header',
  'hgroup',
  'html',
  'i',
  'iframe',
  'ins',
  'kbd',
  'label',
  'legend',
  'li',
  'main',
  'map',
  'mark',
  'menu',
  'meter',
  'nav',
  'noscript',
  'object',
  'ol',
  'optgroup',
  'option',
  'output',
  'p',
  'picture',
  'portal',
  'pre',
  'progress',
  'q',
  'rb',
  'rp',
  'rt',
  'rtc',
  'ruby',
  's',
  'samp',
  'script',
  'section',
  'select',
  'slot',
  'small',
  'source',
  'span',
  'strong',
  'style',
  'sub',
  'summary',
  'sup',
  'svg',
  'table',
  'tbody',
  'td',
  'template',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'title',
  'tr',
  'track',
  'u',
  'ul',
  'var',
  'video',
  'wbr',
]);

const EMPTY_CLASS = 'wf-empty';
const GRID_CLASS = 'wf-grid';
const EMPTY_BLOCK_CLASS = EMPTY_CLASS + '-block';

/**
 * Given a Map of style properties, returns boolean representing whether Map
 * contains styles contributing to dimensions / layout.
 */
const getHasDimensionGivingStyle = (propMap: IMap<string, string>) => {
  const hasDimensionGivingStyle =
    propMap.has('width') ||
    propMap.has('min-width') ||
    propMap.has('padding-left') ||
    propMap.has('padding-right') ||
    propMap.has('border-left-width') ||
    propMap.has('border-right-width') ||
    getHasHeightGivingStyle(propMap) ||
    // if position is absolute or fixed then left/right top/bottom combinations
    // impact layout. Ideally we would check the values here, but we store the
    // applied CSS in a string so we can’t really do much beyond seeing if there
    // is a value applied. We ignore the position prop because combo classes
    // would incorrectly apply the empty class to `static` overrides in all
    // scenarios. Now we only limit it to where positioning was used to force
    // layout constraints.
    (propMap.has('left') && propMap.has('right'));

  return hasDimensionGivingStyle;
};

const getHasHeightGivingStyle = (propMap: IMap<string, string>) => {
  return (
    propMap.has('height') ||
    propMap.has('min-height') ||
    propMap.has('padding-top') ||
    propMap.has('padding-bottom') ||
    propMap.has('border-top-width') ||
    propMap.has('border-bottom-width') ||
    (propMap.has('top') && propMap.has('bottom'))
  );
};

const hasNonEmptyChildren = (data: EvaluatedData): boolean => {
  const evaluatedChildren = getEvaluatedChildren(data);
  const hasOnlyEmptyTextNode =
    evaluatedChildren.length === 1 && evaluatedChildren[0] === '';

  return evaluatedChildren.length > 0 && !hasOnlyEmptyTextNode;
};

const elementHasNonEmptyChildren = (element: ExpressionElement): boolean => {
  const children = getChildren(element);
  const onlyChild = children?.length === 1 ? children[0] : null;

  const hasOnlyEmptyTextNode =
    onlyChild &&
    isStringElement(onlyChild) &&
    Expressions.getText(Expressions.getElementData(onlyChild)) === '';

  return Boolean(children?.length && !hasOnlyEmptyTextNode);
};

/**
 * Determines if the provided element is considered empty, relative to child and style states.
 */
export const isEmpty = (
  element: ExpressionElement,
  mediaQuery: BreakpointID,
  pseudoMode: null | PseudoStateKey,
  variantState: VariantStateKey | null,
  cssPropertyMap: CssPropertyMap,
  data: EvaluatedData | undefined
) => {
  // Renderer callers pass evaluated data, while callers outside of renderer calls
  // this with only the element tree. Evaluated text children are plain strings,
  // while element text children are Basic::String elements that need unwrapping.
  // Prefer the evaluated data when present because it reflects the rendered child shape.
  const hasChildren =
    data !== undefined
      ? hasNonEmptyChildren(data)
      : elementHasNonEmptyChildren(element);

  if (hasChildren) {
    return false;
  }

  // Similar to Block, for Custom element we only show empty if the tag is div
  // and there are no children. This is to improve the adding Custom element experience
  // (via Finder or Add Panel), which defaults to div/no children etc.
  // We assume that users changing the tag and/or updates the children/attributes knows
  // what they are doing and do not need us to aggressively apply empty on every node.
  // Otherwise, we might end up applying empty on nodes that should not have children, like img.
  if (isDOM(element)) {
    return htmlElementsAcceptingChildren.has(
      Expressions.getElementDataAt(
        element,
        [{in: 'Record', at: 'tag'}],
        'Text',
        ''
      ).toLowerCase()
    );
  }

  const propMap =
    getStyleProperties(mediaQuery)(pseudoMode)(variantState)(cssPropertyMap)(
      element
    );

  // Slot is a special case where we want to apply empty class if height is NOT set
  // This is because Slot acts like a drop target for consumers of the component
  if (isSlot(element)) {
    return !getHasHeightGivingStyle(propMap);
  }

  return !getHasDimensionGivingStyle(propMap);
};

/**
 * Generates an Immutable updater that will conditionally add a class for empty nodes
 * @param  {ExpressionElement} element            The element to potentially add the class to.
 * @param  {IMap}   cssPropertyMap     A guid -> Map(cssProperty => Boolean) that keeps track of which CSS properties apply to a style block id.
 * @param  {String}          mediaQuery         Name of the current media query.
 * @param  {Boolean}         showEmptyAsBlock   Boolean to determine if empty node should be shown as block.
 * @param  {Boolean}         showEmptyAsDropTarget   Boolean to determine if empty node is a DropTarget.
 * @return {Function}                           An Immutable updater to apply to an existing List of classes (value: List<T>) => List<T>
 */
export const maybeAddEmptyClass = (
  element: ExpressionElement,
  cssPropertyMap: CssPropertyMap,
  mediaQuery: BreakpointID,
  pseudoMode: PseudoStateKey | null,
  variantState: VariantStateKey | null,
  showEmptyAsBlock: boolean = false,
  data: EvaluatedData | undefined
) => {
  /**
   * Immutable updater that conditionally adds a class for empty nodes to an existing list of classes.
   * @param  {List}  classes  The initial list of classes
   * @return {List}           The resulting list of classes
   */
  const emptyClass = showEmptyAsBlock ? EMPTY_BLOCK_CLASS : EMPTY_CLASS;

  return isEmpty(
    element,
    mediaQuery,
    pseudoMode,
    variantState,
    cssPropertyMap,
    data
  )
    ? push(emptyClass)
    : identity;
};

export const shouldAddEmptyClass = (
  element: ExpressionElement,
  cssPropertyMap: CssPropertyMap,
  mediaQuery: BreakpointID,
  pseudoMode: PseudoStateKey | null,
  variantState: VariantStateKey | null,
  data: EvaluatedData | undefined
): boolean => {
  return isEmpty(
    element,
    mediaQuery,
    pseudoMode,
    variantState,
    cssPropertyMap,
    data
  );
};

export const shouldAddGridClass = (
  element: ExpressionElement,
  cssPropertyMap: CssPropertyMap,
  mediaQuery: BreakpointID,
  pseudoMode: PseudoStateKey | null,
  variantState: VariantStateKey | null
): boolean => {
  return isElementGridDisplay(
    element,
    mediaQuery,
    pseudoMode,
    variantState,
    cssPropertyMap
  );
};

export const maybeAddGridClasses = (
  element: ExpressionElement,
  cssPropertyMap: CssPropertyMap,
  mediaQuery: BreakpointID | null,
  pseudoMode: PseudoStateKey | null,
  variantState: VariantStateKey | null
) => {
  return mediaQuery &&
    isElementGridDisplay(
      element,
      mediaQuery,
      pseudoMode,
      variantState,
      cssPropertyMap
    )
    ? push(GRID_CLASS)
    : identity;
};
